import * as THREE from 'three';
import type { Viewer } from '../../../../viewer/viewer';
import type { IPersona, IViewConfig } from '../../../types';
import { ViewModes } from '../../../helpers';
import { PointoramaRoom } from '../../../../utils/PointoramaRoom';
import { PointoramaStructure } from '../../../../utils/PointoramaStructure';
import { StructureKind } from 'shared-types';
import type { AnnotationKindAdapter, CommitContext, HitTestContext } from './AnnotationKindAdapter';
import type { EditableShape } from './EditableShape';
import type { BasePlaneState, SelectionContextProvider } from './SelectionContextProvider';
import { FloorSelectionContext } from './FloorSelectionContext';
import { ImageCapture } from '../../modules/capture/ImageCapture';
import { CoordinateTransformer } from '../../modules/utils/CoordinateTransformer';
import type { ConstraintSnapReference } from '../../modules/line-detection/LineConstraintSnap';

/**
 * Beam height when the polygon is drawn outside any room — beams don't make
 * sense floating in space, but when the user does it anyway we default to a
 * 3 m extrusion (per spec).
 */
const ORPHAN_BEAM_HEIGHT_DEFAULT = 3.0;

/**
 * Column height when the polygon is drawn outside any room. No ceiling to
 * reach, so default to a typical room height extruded upward from the floor.
 */
const ORPHAN_COLUMN_HEIGHT_DEFAULT = 3.0;

/**
 * Sampled points within this distance of the room/floor base Z are treated as
 * floor leakage (the cloud's floor surface bleeding through the polygon
 * footprint in a top-down ortho) and excluded from the beam-top calculation.
 * 0.2 m comfortably ignores both the floor surface itself and thin floor
 * noise without rejecting a low-hanging structural beam.
 */
const BEAM_FLOOR_EXCLUSION_MARGIN = 0.2;

/**
 * Tight margin used to drop pure ceiling pixels from the sample. The beam top
 * sits just below the ceiling — we only need to reject the ceiling itself,
 * not a 20 cm band that would also throw away genuine beam-top hits. 5 cm
 * comfortably covers point-cloud noise on the ceiling without eating into the
 * beam.
 */
const BEAM_CEILING_EXCLUSION_MARGIN = 0.05;

/**
 * A detected structure only counts as a ceiling BEAM (extruding up to the
 * ceiling) when its measured top surface sits within this top fraction of the
 * room height. If the measured top is lower than that, it isn't hanging from
 * the ceiling — it's reclassified as CUSTOM and extruded from the floor up to
 * the measured point. 0.3 = upper 30% of the room.
 */
const BEAM_UPPER_FRACTION = 0.3;

/**
 * LineTool plug-in for STRUCTURE annotations (beam, column, custom). Drawing
 * uses the same room-style click-to-add-polygon flow on the active floor plane.
 *
 * The structure type (BEAM / COLUMN) is fixed per tool — the Magic Beam and
 * Magic Column tools each construct the adapter with their kind. On commit, the
 * adapter:
 *   1. Resolves the containing room by polygon-centroid XY test.
 *   2. Derives the extrusion height from the room's base/ceiling planes:
 *      - COLUMN: full room height (falls back to a default outside any room).
 *      - BEAM:   position-map sampled top anchored at the room ceiling; falls
 *                back to 3 m when drawn outside any room.
 *   3. Dispatches `create_structure`.
 *
 * No segmentation — structure polygons are pure geometry (like outlines).
 */
export class StructureKindAdapter implements AnnotationKindAdapter {
  /** Structures may only edit structures (inert for MeasureEngine — LineTool-only kind). */
  readonly editDomain = 'structure' as const;

  /** Structures: shift opts into 90° snap (matches rooms). */
  readonly invertShiftFor90Snap = false;

  /**
   * Leave the segmented polygon as editable prompts after auto-detect — the
   * user confirms with Enter, matching the room workflow. Auto-classification
   * (beam vs column) still happens at commit time.
   */
  readonly autoCommitNewFromRegion = false;

  /**
   * Structures reuse the surface segmentation pipeline (not the room one):
   * the surface polygon pass is the right fit for picking out tighter
   * protrusions like beams + columns, and the depth pass falls back to the
   * default line-tool config the same way SurfaceKindAdapter does. The
   * adapter then classifies the committed polygon as BEAM or COLUMN at
   * commit time.
   */
  /**
   * Match the outline tool's editing affordances: the radial edge-op menu
   * (parallel / perpendicular / fixed-distance) and the shared dashed-line
   * constraint snap. Beams and columns are usually axis-aligned to room
   * walls, so the same nudge / snap workflow that helps with outlines also
   * fits structures.
   */
  readonly enableEdgeOpMenu = true;
  readonly enableConstraintSnap = true;

  readonly polygonPipelineConfigPath: string | null = '/config/segmentation/structure-poly-pipeline-config.json';
  readonly depthPipelineConfigPath: string | null = null;
  readonly enableLineDetection = true;

  private floorContext: FloorSelectionContext | null = null;

  /** The structure type this tool creates (Magic Beam / Magic Column). */
  constructor(private kind: StructureKind) {}

  /**
   * Switch the structure type this adapter commits. The magic tools fix their
   * kind at construction, but the manual tool exposes a column/beam toggle in
   * its settings and calls this when the user flips it.
   */
  setKind(kind: StructureKind): void {
    this.kind = kind;
  }

  createSelectionContext(viewer: Viewer, persona: IPersona): SelectionContextProvider {
    this.floorContext = new FloorSelectionContext(viewer, persona);
    return this.floorContext;
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  hitTestEditableTarget(ctx: HitTestContext): EditableShape | null {
    const { viewer, mouse, coordinateTransformer } = ctx;
    const activeFloorId = this.floorContext?.getGroupIdentifier() ?? null;
    // Candidate structures: those on the active floor (fall back to all when the
    // structure has no floor group).
    const structures = (viewer.scene.structures as PointoramaStructure[]).filter((s) => s.visible && s.finished && (activeFloorId == null || s.groupIdentifier == null || s.groupIdentifier === activeFloorId));
    if (structures.length === 0) return null;

    // 1) Outline hit — clicking the structure's edge.
    const hitLines = structures.map((s) => s.backgroundLine);
    const intersect = viewer.inputHandler.raycaster.intersectObjects(hitLines).at(0);
    if (intersect) {
      const onLine = structures.find((s) => s.backgroundLine === intersect.object);
      if (onLine) return onLine;
    }

    // 2) Interior hit — clicking anywhere inside the footprint. Project the mouse
    // to a world point and point-in-polygon test each structure (XY). Prefer the
    // smallest matching footprint so nested/overlapping structures pick the tightest.
    const world = coordinateTransformer.screenToWorld(mouse);
    if (world) {
      let best: PointoramaStructure | null = null;
      let bestArea = Infinity;
      for (const s of structures) {
        const poly = s.getPolygon();
        if (poly.length < 3) continue;
        if (!this.isPointInPolygon(world, poly)) continue;
        const area = this.polygonArea(poly);
        if (area < bestArea) {
          bestArea = area;
          best = s;
        }
      }
      if (best) return best;
    }

    return null;
  }

  /** Absolute XY area of a polygon (shoelace). Used to disambiguate nested footprints. */
  private polygonArea(polygon: THREE.Vector3[]): number {
    let sum = 0;
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i];
      const b = polygon[(i + 1) % polygon.length];
      sum += a.x * b.y - b.x * a.y;
    }
    return Math.abs(sum) / 2;
  }

  isMouseOverEditableShape(ctx: HitTestContext): boolean {
    return this.hitTestEditableTarget(ctx) !== null;
  }

  shouldRunSegmentationOnEnter(_shape: EditableShape): boolean {
    return false;
  }

  configureViewForShape(_shape: EditableShape, _viewer: Viewer): void {
    // Same XRAY_2D as the floor — no view swap.
  }

  restoreViewAfterShape(_shape: EditableShape, _viewer: Viewer): void {
    // Inverse no-op.
  }

  /**
   * Materialize a new structure from the confirmed polygon: find the room
   * containing the polygon centroid, classify beam-vs-column by aspect ratio,
   * resolve the extrusion height from the room, and dispatch the
   * `create_structure` event the `usePotreeEvents` handler consumes.
   */
  commitNew(viewer: Viewer, polygon: THREE.Vector3[], context: CommitContext): void {
    console.log('[StructureKindAdapter] commitNew called', { polygonLen: polygon.length, basePlaneZ: context.basePlane?.origin.z });
    if (polygon.length < 3) {
      console.warn('[StructureKindAdapter] polygon < 3 points, aborting');
      return;
    }

    const floorZ = context.basePlane.origin.z;
    const flatPolygon = polygon.map((v) => new THREE.Vector3(v.x, v.y, floorZ));

    const containingRoom = this.findContainingRoom(viewer, flatPolygon);
    const kind = this.kind;
    console.log('[StructureKindAdapter] commit', { kind, hasRoom: !!containingRoom, roomId: containingRoom?.identifier });

    // No containing room → anchor against the active floor selection's
    // floor/ceiling planes instead of a fixed 3 m default.
    const selectionFloorZ = this.floorContext?.getFloor()?.height ?? floorZ;
    const selectionCeilingZ = this.floorContext?.getCeiling()?.height ?? null;

    let baseZ: number;
    let height: number;
    if (kind === StructureKind.COLUMN) {
      // Column: on the floor, full room height.
      const columnBaseZ = containingRoom?.getBase().position.z ?? selectionFloorZ;
      const columnCeilingZ = containingRoom?.getCeiling().position.z ?? selectionCeilingZ;
      baseZ = columnBaseZ;
      height = columnCeilingZ !== null ? columnCeilingZ - columnBaseZ : ORPHAN_COLUMN_HEIGHT_DEFAULT;
    } else {
      // BEAM: hangs below the ceiling. In a top-down ortho the position map
      // records the FIRST surface the camera hits going downward — for pixels
      // inside the beam's footprint that's the beam top. Take the maximum
      // world Z (= closest to camera) above the floor, then extrude up to the
      // room ceiling so the beam "goes up until the room height".
      const roomFloorZ = containingRoom?.getBase().position.z ?? selectionFloorZ;
      const roomCeilingZ = containingRoom?.getCeiling().position.z ?? selectionCeilingZ;
      const sample = this.sampleBeamTopZInsidePolygon(viewer, flatPolygon, roomFloorZ, roomCeilingZ);
      console.log('[StructureKindAdapter] beam sample', { sample, roomFloorZ, roomCeilingZ });

      if (sample !== null) {
        // We measured the structure's top surface. Decide whether it's actually a
        // ceiling beam: a real beam hangs in the upper part of the room. Compare
        // the measured top against the overhead reference (room ceiling if known,
        // else the cloud's overhead extent inside the polygon). Only the top
        // BEAM_UPPER_FRACTION of that span counts as "near the ceiling".
        const { topZ, overheadZ } = sample;
        const span = overheadZ - roomFloorZ;
        const fractionFromTop = span > 0 ? (overheadZ - topZ) / span : 1;

        if (fractionFromTop <= BEAM_UPPER_FRACTION && overheadZ > topZ) {
          // Near the ceiling → real BEAM: extrude from the measured top up to the
          // overhead reference (room ceiling, or sampled overhead extent).
          baseZ = topZ;
          height = roomCeilingZ !== null ? roomCeilingZ - topZ : ORPHAN_BEAM_HEIGHT_DEFAULT;
        } else {
          // Lower down → not a ceiling beam, but keep its aspect-ratio BEAM
          // classification and extrude from the floor up to the measured top.
          baseZ = roomFloorZ;
          height = Math.max(topZ - roomFloorZ, 0);
        }
      } else if (roomCeilingZ !== null) {
        // Sample missed (no overhead points) — fall back to a full-room-height
        // beam so the user sees something they can resize, instead of a flat
        // polygon on the floor.
        baseZ = roomFloorZ;
        height = roomCeilingZ - roomFloorZ;
      } else {
        // No sample and no ceiling — orphan beam at floor with default height.
        baseZ = floorZ;
        height = ORPHAN_BEAM_HEIGHT_DEFAULT;
      }
    }

    const anchoredPolygon = flatPolygon.map((v) => ({ x: v.x, y: v.y, z: baseZ }));
    console.log('[StructureKindAdapter] dispatching create_structure', { kind, height, baseZ, vertexCount: anchoredPolygon.length });

    viewer.scene.dispatchEvent({
      type: 'create_structure',
      value: {
        polygon: anchoredPolygon,
        height,
        kind,
        roomIdentifier: containingRoom?.identifier ?? null,
        groupIdentifier: this.floorContext?.getGroupIdentifier() ?? null,
      },
    });
  }

  commitEdit(viewer: Viewer, shape: EditableShape, polygon: THREE.Vector3[]): void {
    if (polygon.length < 3 || !(shape instanceof PointoramaStructure)) return;
    // Apply the new footprint to the shape (it keeps its base Z, height, and kind),
    // then persist — classification and height are intentionally NOT recomputed.
    shape.exitEditMode(true, polygon);
    viewer.scene.dispatchEvent({ type: 'structure_updated', structure: shape });
  }

  /**
   * Flatten Z to the active floor height — structures are drawn on the same
   * XY plane as rooms. Beam Z anchoring (ceiling-down) happens at commit time
   * in `commitNew`, not here, so the draw preview tracks the floor.
   */
  snapPolygonToBasePlane(vertices: THREE.Vector3[], context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] {
    if (!context.basePlane) return vertices;
    const floorZ = context.basePlane.origin.z;
    return vertices.map((v) => new THREE.Vector3(v.x, v.y, floorZ));
  }

  getBasePlaneFrame(context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): { origin: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3; normal: THREE.Vector3 } | null {
    if (!context.basePlane) return null;
    return {
      origin: new THREE.Vector3(0, 0, context.basePlane.origin.z),
      u: new THREE.Vector3(1, 0, 0),
      v: new THREE.Vector3(0, 1, 0),
      normal: new THREE.Vector3(0, 0, 1),
    };
  }

  getCaptureBoxCorners(_context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] | null {
    return null;
  }

  /**
   * Constraint-snap references for the LineTool's dashed-line snap: every
   * room wall on the active floor. Identical signal to the outline adapter,
   * since structures share the same "axis-align to room geometry" workflow.
   */
  getConstraintReferences(context: { viewer: Viewer }): ConstraintSnapReference[] {
    const activeFloorId = this.floorContext?.getGroupIdentifier() ?? null;
    if (!activeFloorId) return [];

    const refs: ConstraintSnapReference[] = [];
    for (const room of context.viewer.scene.rooms as PointoramaRoom[]) {
      if (room.groupIdentifier !== activeFloorId) continue;
      const ring = room.getPolygon();
      const n = ring.length;
      for (let i = 0; i < n; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % n];
        refs.push({ start: new THREE.Vector2(a.x, a.y), end: new THREE.Vector2(b.x, b.y) });
      }
    }
    return refs;
  }

  // ─── Position-map sampling ──────────────────────────────────────────

  /**
   * Sample the point cloud's eye-space position map inside the polygon's XY
   * footprint and return the highest world Z that sits meaningfully above the
   * floor and below the ceiling — that's the beam's visible top surface in
   * the top-down ortho.
   *
   * The position map records the FIRST surface the camera hits going downward,
   * so for pixels over the beam's footprint that's the beam top. Floor pixels
   * (visible through gaps around the beam) and ceiling pixels (when the
   * polygon overlaps clear space) are excluded so neither pulls the result
   * away from the beam itself.
   *
   * We track raw min/max Z and the filtered max separately and log all of
   * them so misclassification is easy to diagnose.
   *
   * Returns null when no cloud points project inside the polygon's screen-
   * space bounding box, or when no hits fall inside the floor↔ceiling band.
   */
  private sampleBeamTopZInsidePolygon(viewer: Viewer, polygon: THREE.Vector3[], floorZ: number, ceilingZ: number | null): { topZ: number; overheadZ: number } | null {
    const camera = viewer.scene.getActiveCamera();
    const rendererSize = viewer.renderer.getSize(new THREE.Vector2());
    const screenW = rendererSize.width;
    const screenH = rendererSize.height;

    const screenPts = polygon.map((v) => {
      const ndc = v.clone().project(camera);
      return new THREE.Vector2(((ndc.x + 1) / 2) * screenW, ((1 - ndc.y) / 2) * screenH);
    });
    let minSx = Infinity,
      maxSx = -Infinity,
      minSy = Infinity,
      maxSy = -Infinity;
    for (const p of screenPts) {
      if (p.x < minSx) minSx = p.x;
      if (p.x > maxSx) maxSx = p.x;
      if (p.y < minSy) minSy = p.y;
      if (p.y > maxSy) maxSy = p.y;
    }
    minSx = Math.max(0, Math.floor(minSx));
    minSy = Math.max(0, Math.floor(minSy));
    maxSx = Math.min(screenW, Math.ceil(maxSx));
    maxSy = Math.min(screenH, Math.ceil(maxSy));
    const regionW = maxSx - minSx;
    const regionH = maxSy - minSy;
    if (regionW < 10 || regionH < 10) {
      console.warn('[StructureKindAdapter] sample region too small', { regionW, regionH });
      return null;
    }

    const coordinateTransformer = new CoordinateTransformer(viewer);
    const imageCapture = new ImageCapture(viewer, coordinateTransformer);
    const capture = imageCapture.capturePositionMap(new THREE.Vector2(minSx, minSy), new THREE.Vector2(maxSx, maxSy), regionW, regionH);
    if (!capture) {
      console.warn('[StructureKindAdapter] capturePositionMap returned null');
      return null;
    }
    coordinateTransformer.setPositionMapEncoding({ eyeMin: capture.eyeMin, eyeMax: capture.eyeMax, inverseViewMatrix: capture.inverseViewMatrix });

    const { imageData } = capture;
    const data = imageData.data;
    const imgW = imageData.width;
    const imgH = imageData.height;

    // First pass: gather all in-polygon hits so we can derive a self-relative
    // ceiling exclusion when no room ceiling is known. Top-down ortho almost
    // always sees both the beam top and the surrounding ceiling inside any
    // realistic polygon; picking the absolute max would land on the ceiling,
    // not the beam top.
    const allZ: number[] = [];
    let rawMinZ = Infinity;
    let rawMaxZ = -Infinity;
    for (let py = 0; py < imgH; py++) {
      for (let px = 0; px < imgW; px++) {
        const idx = (py * imgW + px) * 4;
        const a = data[idx + 3];
        if (a === 0 && data[idx] === 0 && data[idx + 1] === 0) continue;
        const world = coordinateTransformer.decodePositionMapPixel(data[idx], data[idx + 1], data[idx + 2], a);
        if (!world) continue;
        if (!this.isPointInPolygon(world, polygon)) continue;
        allZ.push(world.z);
        if (world.z < rawMinZ) rawMinZ = world.z;
        if (world.z > rawMaxZ) rawMaxZ = world.z;
      }
    }

    if (allZ.length === 0) {
      console.warn('[StructureKindAdapter] sample produced no in-polygon hits');
      return null;
    }

    // Floor exclusion is the same as before — anything within a small margin
    // of the floor is treated as floor leakage.
    const floorExclusion = floorZ + BEAM_FLOOR_EXCLUSION_MARGIN;

    // Ceiling exclusion: use the room ceiling when known; otherwise derive a
    // ceiling estimate from the sample itself. The top 5 % of in-polygon Z
    // values usually correspond to the ceiling around the beam, so we drop
    // them before picking the max. This makes the sampler robust to missing
    // ceiling metadata (the orphan case) without flipping back to a 3 m
    // hardcode.
    let ceilingExclusion: number;
    if (ceilingZ !== null) {
      ceilingExclusion = ceilingZ - BEAM_CEILING_EXCLUSION_MARGIN;
    } else {
      const sorted = allZ.slice().sort((a, b) => a - b);
      const p95 = sorted[Math.floor(sorted.length * 0.95)];
      ceilingExclusion = p95 - 0.01;
    }

    // The polygon IS the beam shape — most pixels inside it land on the beam
    // top, with floor leakage around the edges. Pick the statistical mode of
    // the in-band Z distribution: that's the densest cluster, which is the
    // beam top surface by construction.
    const inBand: number[] = [];
    for (const z of allZ) {
      if (z < floorExclusion) continue;
      if (z > ceilingExclusion) continue;
      inBand.push(z);
    }

    let beamTopZ: number | null = null;
    let modeBinCount = 0;
    if (inBand.length > 0) {
      // Manual min/max — inBand can hold tens of thousands of points, and
      // Math.min(...inBand) spreads every element as an argument, overflowing
      // the call stack (RangeError) on large samples.
      let bandMin = inBand[0];
      let bandMax = inBand[0];
      for (const z of inBand) {
        if (z < bandMin) bandMin = z;
        if (z > bandMax) bandMax = z;
      }
      const binSize = 0.02; // 2 cm — fine enough to resolve a thin beam top.
      const binCount = Math.max(1, Math.ceil((bandMax - bandMin) / binSize) + 1);
      const bins = new Array<number>(binCount).fill(0);
      for (const z of inBand) {
        const idx = Math.min(binCount - 1, Math.floor((z - bandMin) / binSize));
        bins[idx]++;
      }
      // Mode = bin with the most hits. That's the dominant surface inside the
      // polygon, which by construction is the beam top the user traced.
      let modeIdx = 0;
      for (let i = 1; i < binCount; i++) {
        if (bins[i] > bins[modeIdx]) modeIdx = i;
      }
      modeBinCount = bins[modeIdx];
      beamTopZ = bandMin + (modeIdx + 0.5) * binSize;
    }

    console.log('[StructureKindAdapter] sample done', {
      totalHits: allZ.length,
      filteredHits: inBand.length,
      modeBinCount,
      rawMinZ,
      rawMaxZ,
      beamTopZ,
      floorZ,
      ceilingZ,
      floorExclusion,
      ceilingExclusion,
      captureSize: { imgW, imgH },
    });
    if (beamTopZ === null) return null;
    // Overhead reference for the "is this near the ceiling?" test: the room
    // ceiling when known, else the cloud's overhead extent inside the polygon.
    const overheadZ = ceilingZ ?? rawMaxZ;
    return { topZ: beamTopZ, overheadZ };
  }

  // ─── Classification + room resolution ────────────────────────────────

  /**
   * Find the room whose base polygon contains the centroid of the drawn
   * structure polygon, scoped to the active floor. Returns null when no room
   * matches (orphan structure).
   */
  private findContainingRoom(viewer: Viewer, polygon: THREE.Vector3[]): PointoramaRoom | null {
    const activeFloorId = this.floorContext?.getGroupIdentifier() ?? null;
    const centroid = this.polygonCentroid(polygon);
    const rooms = viewer.scene.rooms as PointoramaRoom[];

    // Pass 1: prefer rooms on the active floor.
    if (activeFloorId) {
      for (const room of rooms) {
        if (room.groupIdentifier !== activeFloorId) continue;
        const poly = room.getPolygon();
        if (poly.length < 3) continue;
        if (this.isPointInPolygon(centroid, poly)) return room;
      }
    }

    // Pass 2: ignore floor-group filtering. The polygon-containment test is
    // the authoritative "is this point in that room" answer; structure mode
    // shouldn't reject a hit just because the floor group id is stale or
    // unset, otherwise we'd default to a roomless 3 m fallback right next to
    // a clearly correct room.
    for (const room of rooms) {
      const poly = room.getPolygon();
      if (poly.length < 3) continue;
      if (this.isPointInPolygon(centroid, poly)) {
        console.log('[StructureKindAdapter] containing room found via permissive pass (floor-group mismatch)', {
          activeFloorId,
          roomFloorId: room.groupIdentifier,
          roomId: room.identifier,
        });
        return room;
      }
    }

    return null;
  }

  private polygonCentroid(polygon: THREE.Vector3[]): THREE.Vector3 {
    const c = new THREE.Vector3();
    for (const p of polygon) c.add(p);
    return c.divideScalar(polygon.length);
  }

  /**
   * Even-odd point-in-polygon in XY. Identical to RoomKindAdapter's helper.
   */
  private isPointInPolygon(point: THREE.Vector3, vertices: THREE.Vector3[]): boolean {
    let inside = false;
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      const xi = vertices[i].x,
        yi = vertices[i].y;
      const xj = vertices[j].x,
        yj = vertices[j].y;
      if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }
}
